/** @jsxImportSource ../../i18n */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarRange, Coffee, Snowflake, Sprout } from 'lucide-react';
import api from '../../services/api';
import { growthOf, milestonesOf, snowball } from '../../services/snowball';
import { cx } from '../../theme/cx';
import { formatDate, formatNumber, formatPercent } from '../../theme/format';
import { MonthlyBars } from '../charts';
import {
  Badge, Banner, Button, Card, CardHeader, Checkbox, Field, IconTile, Input, Money, SkeletonStat, SymbolMark, Table, TBody, Td, Th, THead, Tr,
} from '../ui';

const pct = (v, decimals = 1) => (v === null || v === undefined ? '—' : formatPercent(v, { sign: false, decimals }));

const SnowballBars = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.reinvested));
  return (
    <ol className="space-y-2" data-testid="snowball-rows">
      {rows.map((r) => (
        <li key={r.year} className="grid grid-cols-[3.5rem_1fr_auto] items-center gap-3 text-sm">
          <span className="text-fg-subtle">An {r.year}</span>
          <span className="relative h-2.5 overflow-hidden rounded-full bg-surface-3" aria-hidden="true">
            <span className="absolute inset-y-0 left-0 origin-left rounded-full bg-fg-faint" style={{ width: `${(r.spent / max) * 100}%` }} />
            <span className="absolute inset-y-0 left-0 origin-left rounded-full bg-gold-500/80 mix-blend-normal" style={{ width: `${(r.reinvested / max) * 100}%`, opacity: 0.85 }} />
          </span>
          <Money value={r.reinvested} decimals={0} className="text-fg" />
        </li>
      ))}
    </ol>
  );
};

export const FutureIncome = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [growth, setGrowth] = useState('');
  const [withDeposit, setWithDeposit] = useState(false);
  const [deposit, setDeposit] = useState('200');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get('/plan/dividends');
      setData(res.data);
      setGrowth(String(growthOf(res.data.growth_pct)).replace('.', ','));
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible d’estimer les revenus.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const g = Number(String(growth).replace(',', '.'));
  const monthly = withDeposit ? Number(String(deposit).replace(',', '.')) || 0 : 0;
  const rows = useMemo(() => (data ? snowball({ income: data.annual_net, yieldPct: data.yield_pct || 0, growth: Number.isFinite(g) ? g : 3, years: 10, monthlyDeposit: monthly }) : []), [data, g, monthly]);
  const milestones = useMemo(() => (data ? milestonesOf(data.annual_net, rows) : []), [data, rows]);
  const calendar = useMemo(() => (data?.calendar || []).map((m) => ({ label: formatDate(`${m.month}-01`, 'month'), value: m.amount })), [data]);

  if (error) return <Banner tone="loss" role="alert" action={<Button size="sm" variant="secondary" onClick={load}>Réessayer</Button>}>{error}</Banner>;
  if (!data) return <div role="status"><span className="sr-only">Estimation des revenus…</span><SkeletonStat /></div>;

  const paying = data.lines.filter((l) => l.annual_income > 0);
  const withheld = data.annual_gross - data.annual_net;
  const last = rows[rows.length - 1];

  return (
    <div className="space-y-6" data-testid="future-income">
      <Card tone="gold" className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <IconTile icon={Sprout} size="lg" />
        <div>
          <p className="eyebrow">Revenus des douze prochains mois</p>
          <p className="mt-1 font-display text-display-md text-fg"><Money value={data.annual_net} decimals={0} /></p>
          <p className="mt-1 text-sm text-fg-muted">soit <Money value={data.monthly_average} decimals={0} className="text-fg" /> par mois en moyenne</p>
        </div>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:ml-auto">
          <dt className="text-fg-muted">Rendement des lignes</dt><dd className="num text-right text-fg">{pct(data.yield_pct, 2)}</dd>
          <dt className="text-fg-muted">Croissance du dividende</dt><dd className="num text-right text-fg">{data.growth_pct === null ? '—' : formatPercent(data.growth_pct)}</dd>
          {withheld > 0.5 && <><dt className="text-fg-muted">Retenu à l’étranger</dt><dd className="text-right"><Money value={withheld} decimals={0} className="text-loss-300" /></dd></>}
        </dl>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <Card>
          <CardHeader icon={CalendarRange} title="Calendrier des versements" description="Chaque dividende des douze derniers mois revient au même mois, au nombre d’actions actuel, net des retenues étrangères." />
          <MonthlyBars data={calendar} height={220} />
        </Card>
        <Card data-testid="snowball">
          <CardHeader icon={Snowflake} title="Boule de neige sur dix ans" description="Dividendes réinvestis (doré) ou dépensés (gris), à rendement constant." />
          <div className="mb-5 grid gap-4 sm:grid-cols-2">
            <Field label="Croissance du dividende" htmlFor="snowball-growth" hint="Historique de vos lignes, borné entre −5 et 10 %.">
              <Input id="snowball-growth" inputMode="decimal" value={growth} onChange={(e) => setGrowth(e.target.value)} trailing={<span className="pr-2 text-sm text-fg-subtle">%/an</span>} />
            </Field>
            <div>
              <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-fg-body">
                <Checkbox checked={withDeposit} onChange={(e) => setWithDeposit(e.target.checked)} />
                Avec un versement mensuel
              </label>
              <Input aria-label="Versement mensuel investi en dividendes" inputMode="decimal" value={deposit} disabled={!withDeposit} onChange={(e) => setDeposit(e.target.value)} trailing={<span className="pr-2 text-sm text-fg-subtle">€/mois</span>} />
            </div>
          </div>
          {data.annual_net > 0 || monthly > 0 ? <SnowballBars rows={rows} /> : <p className="text-sm text-fg-muted">Aucun dividende pour l’instant : la boule de neige démarre avec un premier versement.</p>}
          {last && last.reinvested > 0 && (
            <p className="mt-4 text-sm text-fg-muted">
              Dans dix ans : <Money value={last.reinvested} decimals={0} className="font-medium text-gold-200" /> par an en réinvestissant, contre <Money value={last.spent} decimals={0} className="text-fg" /> en dépensant.
            </p>
          )}
        </Card>
      </div>

      <Card data-testid="milestones">
        <CardHeader icon={Coffee} title="Ce que vos dividendes paient" description="Avec les dividendes réinvestis, l’année où ils couvrent chaque dépense." />
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {milestones.map((m) => (
            <li key={m.id} className={cx('rounded-control border px-4 py-3', m.year === 0 ? 'border-gold-500/30 bg-gold-900/30' : 'border-line bg-surface-2')}>
              <p className="text-sm font-medium text-fg">{m.label}</p>
              <p className="mt-1 text-xs text-fg-subtle"><Money value={m.yearly} decimals={0} sensitive={false} /> par an</p>
              <Badge tone={m.year === 0 ? 'gold' : m.year ? 'gain' : 'neutral'} className="mt-2">
                {m.year === 0 ? 'C’est déjà le cas' : m.year ? `Dans ${m.year} an${m.year > 1 ? 's' : ''}` : 'Au-delà de dix ans'}
              </Badge>
            </li>
          ))}
        </ul>
      </Card>

      {paying.length > 0 && (
        <Card data-testid="income-lines">
          <CardHeader title="Ligne par ligne" description="Dividende des douze derniers mois par action, rendement au cours actuel et sur votre prix de revient." />
          <Table aria-label="Revenus par ligne">
            <THead>
              <Tr>
                <Th>Ligne</Th>
                <Th align="right">Rendement</Th>
                <Th align="right" className="hidden md:table-cell">Sur PRU</Th>
                <Th align="right" className="hidden md:table-cell">Croissance</Th>
                <Th align="right" className="hidden sm:table-cell">Détachement</Th>
                <Th align="right">Net par an</Th>
              </Tr>
            </THead>
            <TBody>
              {paying.map((l) => (
                <Tr key={l.symbol}>
                  <Td className="w-full max-w-0">
                    <div className="flex items-center gap-3">
                      <SymbolMark symbol={l.symbol} size="sm" />
                      <div className="min-w-0">
                        <Link to={`/stock/${encodeURIComponent(l.symbol)}`} className="block truncate font-medium text-fg hover:text-gold-300">{l.symbol}</Link>
                        <p className="truncate text-xs text-fg-subtle">
                          {formatNumber(l.per_share_ttm, { decimals: 2 })} {l.currency === 'EUR' ? '€' : l.currency} par action · {l.payments_per_year} versement{l.payments_per_year > 1 ? 's' : ''}
                          {l.withholding_pct > 0 && ` · retenue ${formatPercent(l.withholding_pct, { sign: false, decimals: 1 })}`}
                        </p>
                      </div>
                    </div>
                  </Td>
                  <Td align="right" className="num">{pct(l.yield_pct, 2)}</Td>
                  <Td align="right" className="num hidden md:table-cell">{pct(l.yield_on_cost_pct, 2)}</Td>
                  <Td align="right" className="num hidden md:table-cell">{l.growth_pct === null ? '—' : formatPercent(l.growth_pct)}</Td>
                  <Td align="right" className="num hidden whitespace-nowrap sm:table-cell">{l.next_ex_date ? formatDate(l.next_ex_date) : '—'}</Td>
                  <Td align="right" className="whitespace-nowrap"><Money value={l.net_income} decimals={0} /></Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <div className="space-y-2 text-xs leading-relaxed text-fg-subtle">
        {data.not_eur.length > 0 && <p>Lignes en devise étrangère hors totaux : {data.not_eur.join(', ')}.</p>}
        {data.unavailable.length > 0 && <p>Historique de dividendes indisponible pour {data.unavailable.join(', ')}.</p>}
        <p>Estimation à partir des dividendes des douze derniers mois (Yahoo Finance, date de détachement ; le versement suit de quelques jours). Les dividendes futurs ne sont pas garantis ; la croissance passée est indicative.</p>
      </div>
    </div>
  );
};

export default FutureIncome;
